import { useToast, useToastSichtbar } from "../design/toastKontext";
import { ApiFehler, fehlerText, planWeg } from "../api/client";
import { Archive, ArrowDown, AtSign, ChevronRight, Inbox, Mail, MailWarning, Plus, ScanText } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router";
import {
  alleEintraege,
  useAufgabeErledigt,
  useAufgaben,
  useDokumente,
  useDokumenteSeiten,
  useEingangMails,
  useFinanzen,
  useFristen,
  useIch,
  useKennenlernen,
  usePersonen,
} from "../api/abfragen";
import { useNamen } from "../api/namen";
import type { Aufgabe, Dokument, DokumentDetail, Frist } from "../api/typen";
import { eroeffnungStarten, ZELLEN, type Ablauf, type EroeffnungDaten } from "../buehne/eroeffnung";
import { DokumentZeile } from "../design/DokumentZeile";
import { Gruppe, Zeile, ZEILE_KLASSE } from "../design/Gruppe";
import { Kachel } from "../design/Kachel";
import { Knopf } from "../design/Knopf";
import { Fehlertext, Laedt, Leer, WeitereLaden } from "../design/Leer";
import { Seite } from "../design/Seite";
import { NeueAufgabe } from "./NeueAufgabe";
import { Anzahl, FristWort } from "./AufgabenTeile";
import { useBreit, useWeit } from "../design/useBreit";
import { betrag } from "../format";
import { fokusUebernehmen } from "./ablegen";
import { FreigabeRueckfall } from "./FreigabeRueckfall";
import { useAnker } from "./anker";
import { ALLE_ABGELEGT } from "./archiv";
import { fristWoher } from "./fristWoher";
import { FristZeile } from "./Fristen";
import { FristNeuBlatt } from "./FristNeuBlatt";
import { useFristBlatt } from "./fristNeu";
import { Vorschlagskarte } from "./Vorschlagskarte";
import { kartenZitat } from "./vorschlag";
import { KartenPlatz } from "./KartenPlatz";
import { useKarteHalten } from "./karteHalten";
import { Satz } from "../welt/Blase";
import type { SatzTeil } from "../welt/blasenForm";
import type { Naechstes } from "../welt/saetze";
import { BLENDE_REDUZIERT_W82, DAUER, KURVE, bewegungReduziert, kurve, kurveFuer, spiel, still } from "../welt/bewegung";
import { darf, zeigenStopps } from "../welt/darf";
import { auftrittHeute, auftrittMerken, einfuehrungImBesuch, hinweisPraesenzGesehen, startseiteTagZaehlen, startseitenTageMitHeute, useWelt, type Fassung, type Praesenz } from "../welt/einstellungen";
import { FristenModul } from "../welt/FristenModul";
import { SAEULEN_AB, useModulAuftritt, type ModulHalt } from "../welt/sichtbarkeit";
import { AUFTRITT_NACH_BEGRUESSUNG_MS, aufBegruessungWarten, begruessungKommt } from "./rueckmeldung/begruessung";
import { GeistEbene } from "../welt/GeistEbene";
import { useGeist } from "../welt/geistKontext";
import { GeistSzene } from "../welt/GeistSzene";
import { WeltWurzel } from "../welt/WeltWurzel";
import { Kopfzeile, Lupe } from "../welt/Kopfzeile";
import { aufgabeIm, dinge, erklaert, fristenBald, fristZustand, heuteBerlin, offenLage, tageBis } from "../welt/lage";
import { OffenBlock } from "../welt/OffenBlock";
import { usePersonAnsicht, usePersonFristen } from "../welt/personAnsicht";
import { PraesenzHinweis } from "../welt/PraesenzHinweis";
import { AnsichtZeile } from "../welt/AnsichtZeile";
import { FRISTNAME, knopfZeigen, kopfVorschlag, satzAntwort, satzKeinBrief, satzKopf, satzKopfStart, satzNichtsBald, satzNichtsUeberfaellig, satzUnterwegs, schrittText, vornameAus, zeileKopf } from "../welt/saetze";

/** W82 Beta-W2 (D-704): Ref des Fristen-Moduls, solange die Begrüßung vor dem Auftritt steht (misst und startet nichts). */
const ohneMessen = () => undefined;

/** W82 Design-Web-1 (DWW-1): die erste Säule des Fristen-Moduls wächst so lange nach dem Einschweben der Module (Eröffnung). */
const MODUL_NACH_EINSCHWEBEN = 200;

/** R1 5f: Zellen, deren Zahl bei gewählter Person erst nach ihren Daten feststeht (gedämpft wie vor dem Rundflug). */
const DUNKEL_LAEDT: ReadonlySet<string> = new Set(["ueberfaellig", "bald", "pruefen"]);

const LINK = "t-body inline-flex min-h-(--touch) min-w-(--touch) items-center justify-end gap-1 text-pg-accent-text hover:opacity-70";

/** Dauer des Auftritts je Fassung und Stufe bis zum Endzustand (DESIGN-W82 „Auftritt beim Öffnen“); danach werden die
 *  Auftrittsklassen entfernt (D-420). Der Bildschirmwechsel kommt 460 ab 90 (ruhig 440 ab 80). */
function auftrittDauer(art: "voll" | "wechsel", fassung: Fassung, praesenz: Praesenz): number {
  if (art === "wechsel") return fassung === "ruhig" ? 520 : 550;
  if (fassung === "ruhig") return praesenz === "ueberall" ? DAUER.auftritt.ruhig.fertig : praesenz === "erklaeren" ? DAUER.auftritt.ruhigErklaeren.fertig : DAUER.auftritt.ruhigKeine.fertig;
  return praesenz === "ueberall" ? 3400 : praesenz === "erklaeren" ? 1800 : DAUER.auftritt.lebhaftKeine.einblenden;
}

/** Start (W82 Teil 2, E1, E14, Bühnen-Regel): der Posteingang ist die Startseite der W82-Welt (`data-welt` an der
 *  Wurzel). Reihenfolge: Kopfzeile mit Profil, Datum und Lupe, Kopf mit Geist, Offen-Block, Fristen-Modul mit Säulen und
 *  der D-333-Liste, Aufgaben, „Bitte prüfen“ mit Stimme, die neue Post (Gruppen des Bestands). Am Schreibtisch stehen
 *  Fristen und Aufgaben rechts. Texte, Kennungen und Verhalten des Bestands bleiben (Suche, Person, Neue Aufgabe, Archiv,
 *  Post per Mail am Schreibtisch, Rückgängig). */
export function Posteingang() {
  return (
    <WeltWurzel kennung="startseite-wurzel">
      <GeistEbene>
        <Startseite />
      </GeistEbene>
    </WeltWurzel>
  );
}

function Startseite() {
  const { data: ich } = useIch();
  const navigieren = useNavigate();
  const breit = useBreit();
  const weit = useWeit();
  const [welt] = useWelt();
  const geist = useGeist();
  // W82 Web-5f (D-562, H2): die Wahl kommt aus dem Menü am Kürzel (Anbieter in der Schale), nicht mehr aus dem Segment
  const { person } = usePersonAnsicht();
  // R1 W82 Web-5h3 (HW15): seit er am Ziel bleibt, bis „Danke“ kommt, schickt ein Wechsel der Ansicht Figur und Blase heim; ihr
  // Ziel gehört zur vorigen Ansicht (sonst stünde die Blase über der Reiterleiste und die Figur über fremdem Text)
  const personVorher = useRef(person);
  useEffect(() => {
    if (personVorher.current === person) return;
    personVorher.current = person;
    geist.heim();
  }, [person, geist]);
  const filter = person === "alle" ? {} : { person_id: person };
  const pruefenSeiten = useDokumenteSeiten({ status: "pruefen", ...filter });
  const pruefenListe = alleEintraege(pruefenSeiten.data);
  const pruefenGesamt = pruefenSeiten.data?.pages[0]?.gesamt ?? 0;
  const zuletzt = useDokumente({ ...filter, pro_seite: 30 });
  // Stack-R2: „Neue Post“ nimmt die abgelegten Briefe aus den ersten 30 aller Status (neueste zuerst). Warten viele Briefe in
  // „Bitte prüfen“, bleiben dort wenige oder keine übrig, obwohl abgelegte da sind: dann fragt die Startseite sie selbst ab
  const ersteAbgelegt = (zuletzt.data?.eintraege ?? []).filter((d) => d.status === "archiviert");
  const ersteListe = zuletzt.data;
  const ueberlauf = ersteListe !== undefined && ersteListe.gesamt > ersteListe.eintraege.length && ersteAbgelegt.length < NEUE_POST_MENGE;
  const abgelegtNeu = useDokumente({ ...filter, status: "archiviert", pro_seite: NEUE_POST_MENGE }, ueberlauf);
  const fristen = useFristen(true);
  const fristenAlle = useFristen(false);
  // Zahl zu pruefen fuer Lage und Abzeichen: alle Personen (wie die Reiterleiste)
  const pruefenAlle = useDokumente({ status: "pruefen", pro_seite: 1 });
  const finanzen = useFinanzen();
  // K36 (W82 Web-5b-N1): offene Aufgaben mit Fälligkeit zählen in der Lage wie Fristen (dieselbe Abfrage wie die Liste)
  const aufgaben = useAufgaben();
  const { data: personen } = usePersonen();
  const mehrere = (personen?.length ?? 0) > 1;
  const namen = useNamen();
  const rueckfragen = (useEingangMails("zweifel", true).data ?? []).length;
  const kennenlernen = useKennenlernen().data;

  const heute = heuteBerlin();
  const alleOffen = useMemo(() => fristen.data ?? [], [fristen.data]);
  const alleAufgaben = useMemo(() => aufgaben.data ?? [], [aufgaben.data]);
  // W82 Web-5f (BS09, FW18, D-586 Auflage 2): bei gewählter Person folgen Kacheln, Kopfsatz, Modul, Säulen, Aufgaben und
  // Rundgang ihren Fristen (Person aus dem Feld des Servers, Regel R-P2 in `personAnsicht.ts`, W82 Web-5h1) und ihrer Zahl zu
  // prüfen; Fixkosten bleiben der
  // Haushalt (Akten tragen keine Person)
  const jePerson = usePersonFristen(person, { offen: alleOffen, alle: fristenAlle.data, aufgaben: alleAufgaben, geladen: fristen.isSuccess });
  const offenFristen = jePerson.offen;
  const offenAufgaben = jePerson.aufgaben;
  const gewaehlt = person === "alle" ? null : (personen?.find((p) => p.id === person) ?? null);
  const gewaehltName = gewaehlt ? (gewaehlt.name.split(" ")[0] ?? gewaehlt.name) : null;
  const pruefenZahl = person === "alle" ? (pruefenAlle.data?.gesamt ?? 0) : pruefenGesamt;
  const lage = offenLage(
    { fristen: offenFristen, aufgaben: offenAufgaben, pruefen: pruefenZahl, fixkosten: finanzen.data ? Number(finanzen.data.monatlich) || null : null },
    heute,
  );
  const istErklaert = erklaert(lage);
  const bald = fristenBald(offenFristen, heute);
  // R2 W82-Web-5b (K36): offene Aufgaben in Überfällig plus Bald; die Antworten nennen sie nicht „Frist“ (`satzAntwort`)
  const nurAufgaben = offenLage({ fristen: [], aufgaben: offenAufgaben, pruefen: 0, fixkosten: null }, heute);
  const aufgabenDran = nurAufgaben.ueberfaellig + nurAufgaben.bald;
  const vorname = vornameAus(ich?.nutzer.name);
  // R2 W82-Web-5b (K36): die Aufgaben zählen in der Lage; scheitern sie, nennt der Kopf den Fehler statt „Alles erledigt“
  // R1 W82-Web-5h2: ebenso die Fixkosten; sie tragen jetzt `lageBereit`, ohne sie schwieg der Kopf ganz (kein Satz, kein Fehler)
  const lageFehler = fristen.error ?? pruefenAlle.error ?? aufgaben.error ?? finanzen.error ?? null;
  // die Lage steht, wenn Fristen, Zahl zu prüfen, Fixkosten und Aufgaben da sind (sonst rollte „0 €“ gleich nach dem Laden auf
  // den Betrag, und der Kopf sagte kurz „Alles erledigt“, bevor die Aufgaben zählen)
  // W82 Web-5f: der Wechsel der Person (gefilterte Zahl zu prüfen) zählt nicht hinein; kippte `lageBereit` dabei auf falsch, begänne der
  // Auftritt (Rundflug, Zählen) beim nächsten `lageBereit` von vorn
  // W82 Web-5h2 (O1, V07, entscheide D2): ohne Daten keine Zahl. `lageBereit` (Fristen, Zahl zu prüfen, Fixkosten und Aufgaben
  // erfolgreich) trägt Kacheln, Kopfzeile, Knopf und Auftritt; scheitern die Aufgaben, fehlen die Kacheln wie beim Fehler der
  // Fristen und der Kopf nennt den Fehler. `modulBereit` (Fristen erfolgreich) trägt das Fristen-Modul; es hängt sich erst ein, wenn
  // auch die übrigen Abfragen geantwortet haben (gleich ob mit Erfolg), sonst mäße es seine Sichtbarkeit (FW21, 60 %) ohne die
  // Kacheln darüber und begänne zu früh
  const lageBereit = fristen.isSuccess && pruefenAlle.isSuccess && finanzen.isSuccess && aufgaben.isSuccess;
  const modulBereit = fristen.isSuccess && !pruefenAlle.isPending && !finanzen.isPending && !aufgaben.isPending;
  // R1 5f (bleibt als Schutz, W82 Web-5h1): bei gewählter Person steht die Lage erst, wenn ihre Fristen und ihre Zahl zu prüfen da
  // sind; bis dahin sagt der Kopf nichts über sie, der Knopf fehlt, ein Tipp wirkt nicht (sonst „Für Ben ist nichts überfällig.“
  // aus halben Daten)
  const ansichtBereit = person === "alle" || (jePerson.bereit && pruefenSeiten.isSuccess);

  const inArbeit = (zuletzt.data?.eintraege ?? []).filter((d) => ["eingang", "verarbeitung", "fehler"].includes(d.status));
  // N4 (D-453, Vorlage R11 N1 „Neue Post 3“): die drei neuesten abgelegten Briefe, nur Einsortiertes (FW9)
  const archiviert = (ueberlauf ? (abgelegtNeu.data?.eintraege ?? ersteAbgelegt) : ersteAbgelegt).filter((d) => d.status === "archiviert").slice(0, NEUE_POST_MENGE);
  const fristJeDokument = useMemo(() => {
    const m = new Map<string, Frist>();
    for (const f of fristen.data ?? []) if (f.dokument_id && !m.has(f.dokument_id)) m.set(f.dokument_id, f);
    return m;
  }, [fristen.data]);
  const leer = zuletzt.data && zuletzt.data.gesamt === 0 && person === "alle";
  const [erster, ...weitere] = pruefenListe;
  // F2: ohne naechste Karte nimmt die Liste „Neue Post“ den Fokus der erledigten Karte, ohne
  // Liste die Seitenueberschrift (die Gruppe „Bitte pruefen“ verschwindet mit der letzten Karte)
  const ohneKarte = pruefenSeiten.isSuccess && !erster;
  // W82 Web-5h3 (HW9): Fläche der Karte nach „Passt“, bis der Gaist geantwortet hat
  const { halt, halten } = useKarteHalten();
  useEffect(() => {
    if (ohneKarte) fokusUebernehmen(document.querySelector<HTMLElement>("#archiviert-liste") ?? document.querySelector<HTMLElement>("main h1"));
  }, [ohneKarte]);

  // ---------- Auftritt (W82 Web-5h2, Frage 44 = A: voll nur beim ersten Öffnen des Tages je Konto, sonst Bildschirmwechsel) ----------
  // `Angemeldet` (App.tsx) zeigt die Seite erst mit bekanntem Konto; fehlt es doch, gilt der Merker des Besuchs
  const konto = ich?.nutzer.id ?? null;
  const [auftrittArt] = useState<"voll" | "wechsel" | null>(() => (still() || bewegungReduziert() ? null : auftrittHeute(konto, heute)));
  // W82 Beta-W2 (D-704): kommt die Beta-Begrüßung, öffnet sie zuerst; der Auftritt steht so lange im ersten Bild (welt.css
  // `data-auftritt-wartet`) und startet `AUFTRITT_NACH_BEGRUESSUNG_MS` nach „Verstanden“, Takte und Dauern wie sonst
  const [vorBegruessung, setVorBegruessung] = useState(() => auftrittArt !== null && begruessungKommt(ich, "/"));
  useEffect(() => {
    if (!vorBegruessung) return;
    let uhr: ReturnType<typeof setTimeout> | undefined;
    const ab = aufBegruessungWarten(() => (uhr = setTimeout(() => setVorBegruessung(false), AUFTRITT_NACH_BEGRUESSUNG_MS)));
    return () => {
      ab();
      clearTimeout(uhr);
    };
  }, [vorBegruessung]);
  // R1 W82 Web-Beta-W2 (Abweichung 7): hält eine Meldung die Begrüßung auf („Angemeldet als …“ nach dem Anmelden per Link,
  // 9 s; sie öffnet nie über einer Meldung), wartet der Auftritt nicht im ersten Bild: die Seite stünde so lange leer. Er läuft
  // wie ohne Begrüßung, sie kommt nach der Meldung. Die Wache gilt nur, solange die Begrüßung gleich öffnet oder offen ist.
  const meldung = useToastSichtbar();
  useEffect(() => {
    if (!vorBegruessung || !meldung || document.querySelector('[data-testid="beta-begruessung"]')) return;
    const uhr = setTimeout(() => setVorBegruessung(false), 0);
    return () => clearTimeout(uhr);
  }, [vorBegruessung, meldung]);
  const auftrittFrei = lageBereit && !vorBegruessung;
  // Rundflug durch die Lichtkacheln (lebhaft, Überall): bis er sie anstößt, steht ihre Zahl bei 30 %
  // W82 Design-Web-1 (D-1661, DWW-1): an dieser Stelle spielt jetzt die Eröffnung nach dem iOS-Vorbild (buehne/eroeffnung.ts),
  // gleiche Bedingung (`darf("rundflug")`: lebhaft, Überall, erstes Öffnen des Tages); bis ein Brief in der Kachel landet, steht
  // ihre Zahl gedimmt auf 0, dann rollt sie
  const [rundflug] = useState(() => auftrittArt === "voll" && darf("rundflug", { fassung: welt.fassung, praesenz: welt.praesenz, erklaert: false }));
  const [auftrittVorbei, setAuftrittVorbei] = useState(false);
  // Stufe der Eröffnung an der Wurzel (buehne.css): „wartet“ hält Kacheln, Figur und Module im ersten Bild, „laeuft“ führt die
  // Bühne, „fertig“ nimmt nur den vollen Auftritt des Bestands aus, bis `data-auftritt` geht (sonst spielte er nach dem Ende neu)
  const [eroeffnungStufe, setEroeffnungStufe] = useState<"wartet" | "laeuft" | "fertig">("wartet");
  const [warten, setWarten] = useState<ReadonlySet<string>>(() => new Set(rundflug ? ZELLEN : []));
  const [zaehlenAus, setZaehlenAus] = useState(false);
  const hochzaehlen = auftrittArt === "voll" && welt.fassung === "lebhaft" && welt.praesenz !== "keine";
  // Daten der Eröffnung: nur, was die Startseite ohnehin lädt (Kachelzahlen, Absender, erster Vorschlag)
  const eroeffnungDaten: EroeffnungDaten = (() => {
    const dokumentJe = new Map<string, Dokument>([...pruefenListe, ...(zuletzt.data?.eintraege ?? []), ...archiviert].map((d) => [d.id, d]));
    const name = (d: Dokument | undefined) => (d ? (namen.absender(d.absender_id) ?? undefined) : undefined);
    const nachFrist = (z: "ueberfaellig" | "bald") =>
      offenFristen.filter((f) => fristZustand(f, heute) === z).map((f) => name(f.dokument_id ? dokumentJe.get(f.dokument_id) : undefined)).filter((n): n is string => Boolean(n));
    const fern = [...new Set((zuletzt.data?.eintraege ?? []).map(name).filter((n): n is string => Boolean(n)))];
    return {
      anzahl: { ueberfaellig: lage.ueberfaellig, bald: lage.bald, pruefen: lage.pruefen, fixkosten: lage.fixkosten ?? 0 },
      namen: {
        ueberfaellig: nachFrist("ueberfaellig"),
        bald: nachFrist("bald"),
        pruefen: pruefenListe.slice(1).map(name).filter((n): n is string => Boolean(n)),
        fixkosten: archiviert.map(name).filter((n): n is string => Boolean(n)),
      },
      fern,
      held: erster ? { name: name(erster) ?? "Neuer Brief", betreff: erster.betreff ?? "Ihr Schreiben", zeile: erster.betreff ?? "Bitte prüfen" } : null,
    };
  })();
  const eroeffnungRef = useRef(eroeffnungDaten);
  const ersterRef = useRef(erster);
  useLayoutEffect(() => {
    eroeffnungRef.current = eroeffnungDaten;
    ersterRef.current = erster;
  });
  // FW21: das Fristen-Modul spielt seine Choreografie erst, wenn es zu 60 % im Bild ist (auch nach Rollen oder „Zeig sie mir“)
  // Design-Web-1: mit der Eröffnung hält es, bis die Module einschweben; die erste Säule wächst 200 ms danach
  const [modulHalt, setModulHalt] = useState<ModulHalt>(rundflug ? "halten" : "frei");
  const modul = useModulAuftritt(hochzaehlen, rundflug ? MODUL_NACH_EINSCHWEBEN : SAEULEN_AB[welt.praesenz === "erklaeren" ? "erklaeren" : "ueberall"], modulHalt);
  const qc = useQueryClient();
  const startseite = useRef<HTMLDivElement>(null);
  const auftritt = lageBereit && !auftrittVorbei ? auftrittArt : null;
  const auftrittWartet = auftritt !== null && vorBegruessung;
  const [dunkel, setDunkel] = useState<ReadonlySet<string>>(() => new Set(rundflug ? ["ueberfaellig", "bald", "pruefen", "fixkosten"] : []));
  // W82 Web-6a-N1 (R1 B1, WN-39, iOS D-1133): mit Rundflug steht die Kopfblase bis zum Ende des Rundflugs verborgen (welt.css
  // `data-rundflug="wartet"`) und blendet dann mit der Heimkehr ein, statt ihren Auftritt unsichtbar hinter dem Flug zu spielen
  // Endet das Warten, ohne dass er geflogen ist (Tipp vor dem ersten Flug, keine Kachel im Bild), gibt es keine Landung: die Blase
  // steht dann ohne Heimkehr-Blende da (`data-rundflug="sofort"` für zwei Bilder), wie vor dem Rundflug-Auftritt
  const [rundflugWartet, setRundflugWartet] = useState(rundflug);
  const [rundflugSofort, setRundflugSofort] = useState(false);
  const wartet = useRef(rundflug);
  const fliegt = useRef(false);
  useLayoutEffect(() => {
    wartet.current = rundflugWartet;
    fliegt.current = geist.unterwegs;
  });
  const wartenEnde = () => {
    if (wartet.current && !fliegt.current) setRundflugSofort(true);
    wartet.current = false;
    setRundflugWartet(false);
  };
  useEffect(() => {
    if (!rundflugSofort) return;
    let takt = requestAnimationFrame(() => (takt = requestAnimationFrame(() => setRundflugSofort(false))));
    return () => cancelAnimationFrame(takt);
  }, [rundflugSofort]);
  const gestartet = useRef(false);
  useEffect(() => {
    if (!auftrittFrei || gestartet.current || !auftrittArt) return;
    gestartet.current = true;
    // W82 Beta-W2 (D-704): der Tag gilt erst, wenn der Auftritt sichtbar beginnt (ein Kontowechsel hängt die Startseite neu ein)
    if (konto) auftrittMerken(konto, heute);
    const uhren: ReturnType<typeof setTimeout>[] = [];
    let aus = false;
    let ablauf: Ablauf | null = null;
    const ende = () => {
      if (aus) return;
      aus = true;
      uhren.forEach(clearTimeout);
      // Design-Web-1: ein Tipp schneidet die Eröffnung in die Endlage (nach ihrem Ende tut das nichts mehr)
      ablauf?.schneiden();
      setAuftrittVorbei(true);
      setDunkel(new Set());
      setWarten(new Set());
      // Design-Web-1: hielt die Eröffnung das Modul noch, ist es jetzt frei (FW21)
      setModulHalt((h) => (h === "halten" ? "frei" : h));
      wartenEnde();
      if (rundflug) geist.heim();
    };
    const dauer = auftrittDauer(auftrittArt, welt.fassung, welt.praesenz);
    if (!rundflug) uhren.push(setTimeout(ende, dauer));
    if (rundflug) {
      // W82 Design-Web-1 (DWW-1): die Eröffnung statt des Rundflugs. Sie endet selbst (Plan, etwa 6,3 s) und meldet die Takte, an
      // denen die Startseite hängt: Zahl der Kachel rollt bei der ersten Landung, die Kopfblase wächst nach dem Hüpfer
      const wurzel = startseite.current;
      const starten = () => {
        if (aus || !wurzel) return;
        const daten = { ...eroeffnungRef.current };
        const erst = ersterRef.current;
        if (erst && daten.held) {
          const detail = qc.getQueryData<DokumentDetail>(["dokument", erst.id]);
          daten.held = { ...daten.held, betreff: detail?.betreff ?? daten.held.betreff, zeile: kartenZitat(detail?.vorschlag) ?? daten.held.zeile };
        }
        wurzel.dataset.eroeffnung = "laeuft";
        setEroeffnungStufe("laeuft");
        ablauf = eroeffnungStarten(wurzel, daten, {
          landung: (z) => {
            setDunkel((alt) => new Set([...alt].filter((x) => x !== z)));
            setWarten((alt) => new Set([...alt].filter((x) => x !== z)));
          },
          module: () => setModulHalt("frei"),
          blase: () => {
            wartet.current = false;
            setRundflugWartet(false);
          },
          ende: (geschnitten, art) => {
            // im selben Bild wie das Abräumen der Bühne: ohne Halten, ohne neuen Auftritt des Bestands
            wurzel.dataset.eroeffnung = "fertig";
            setEroeffnungStufe("fertig");
            if (geschnitten) {
              // Zahlen sofort auf der Lage; das Modul wie im Bestand: nach Tipp oder Taste fertig, nach Rollen spielt es bei 60 %
              setZaehlenAus(true);
              setModulHalt(art === "click" || art === "keydown" ? "ende" : "frei");
            }
            ende();
          },
        });
      };
      // höchstens 0,5 s auf das Detail des ersten Vorschlags warten, wenn die Karte es gerade holt (iOS PosteingangAnsicht:1388-1390)
      const id = ersterRef.current?.id;
      if (id && !qc.getQueryData(["dokument", id]) && qc.isFetching({ queryKey: ["dokument", id] }) > 0) {
        const bis = performance.now() + 500;
        const nachsehen = () => {
          if (aus) return;
          if (qc.getQueryData(["dokument", id]) || performance.now() >= bis) starten();
          else uhren.push(setTimeout(nachsehen, 50));
        };
        uhren.push(setTimeout(nachsehen, 50));
      } else starten();
    }
    // Unterbrechen-Regel: ein Tipp oder eine Taste beendet den Auftritt im Endzustand. Auf den abgeschlossenen Tipp
    // (click), nicht schon beim Aufsetzen: sonst springt der Inhalt zwischen Aufsetzen und Loslassen und der Tipp geht ins Leere
    const tipp = () => {
      ende();
      // Design-Web-1: nach einem Tipp steht ein Modul im Bild fertig, wie `abbrechen` im Bestand
      if (rundflug) setModulHalt("ende");
      modul.abbrechen();
      if (auftrittArt === "voll") geist.heim();
    };
    window.addEventListener("click", tipp, { capture: true, once: true });
    window.addEventListener("keydown", tipp, { capture: true, once: true });
    return () => {
      aus = true;
      gestartet.current = false;
      uhren.forEach(clearTimeout);
      ablauf?.abbrechen();
      window.removeEventListener("click", tipp, { capture: true });
      window.removeEventListener("keydown", tipp, { capture: true });
    };
    // einmal je Einhängen, sobald die Lage steht und keine Begrüßung mehr davor
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auftrittFrei]);

  // ---------- Präsenz-Hinweis (E13): einmal, als zweite Blase nach dem ersten Kopfsatz, nur bei „Überall“ ----------
  // Web-5d K9: erst nach etwas Nutzung, am dritten Kalendertag mit geöffneter Startseite (Europe/Berlin), nie im Besuch der
  // Einführung; der Tag zählt beim Einhängen, der Speicher ändert sich nicht während des Renderns
  const [hinweisOffen, setHinweisOffen] = useState(
    () => welt.praesenz === "ueberall" && !hinweisPraesenzGesehen() && startseitenTageMitHeute() >= 3 && !einfuehrungImBesuch(),
  );
  useEffect(() => startseiteTagZaehlen(), []);
  const [hinweisDa, setHinweisDa] = useState(false);
  useEffect(() => {
    if (!auftrittFrei || !hinweisOffen || hinweisDa) return;
    // Design-Web-1: mit der Eröffnung kommt der Hinweis, wenn sie vorbei ist (ihr Ende hängt am Plan)
    if (rundflug && !auftrittVorbei) return;
    const uhr = setTimeout(() => setHinweisDa(true), auftrittArt === "voll" && !rundflug ? auftrittDauer("voll", welt.fassung, welt.praesenz) : 0);
    return () => clearTimeout(uhr);
  }, [auftrittFrei, hinweisOffen, hinweisDa, auftrittArt, welt.fassung, welt.praesenz, rundflug, auftrittVorbei]);

  // ---------- Kopf mit Geist ----------
  const [antwort, setAntwort] = useState<SatzTeil[] | null>(null);
  const antwortUhr = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(antwortUhr.current), []);
  const fehlerImKopf = lageFehler !== null && darf("fehler", { fassung: welt.fassung, praesenz: welt.praesenz, erklaert: true });
  const ziele = {
    ueberfaellig: lage.ueberfaellig > 0,
    bald: lage.bald > 0,
    vorschlag: Boolean(erster),
  };
  const stopps = darf("zeigen", { fassung: welt.fassung, praesenz: welt.praesenz, erklaert: istErklaert }) ? zeigenStopps(welt.fassung, welt.praesenz, ziele) : [];
  // W82 Web-5h2 (HW7, HW16): ohne gewählte Person nennt der Kopf lebhaft das Nächste, ruhig zuerst den Brief, den der Knopf zeigt;
  // bei gewählter Person bleibt der Satz aus 5h-W1 (Z20 bis Z22)
  const satz: SatzTeil[] | null = fehlerImKopf
    ? [{ text: fehlerText(lageFehler) }] // nie der Text des Browsers („Failed to fetch“), Regel V75w R1 A2
    : !lageBereit || !ansichtBereit
      ? null
      : (antwort ??
        (gewaehltName
          ? satzKopf(lage, vorname, welt.fassung, gewaehltName)
          : satzKopfStart(lage, vorname, welt.fassung, { heute, naechstes: naechstes(), zeigtBrief: stopps[0] === "vorschlag", sachen: aufgabenDran > 0 })));
  // K21 (W82 Web-5b): der Knopf passt zum Kopfsatz, der dieselbe Zahl nennt (`dinge`)
  const knopf = lageBereit && ansichtBereit && !fehlerImKopf ? knopfZeigen(stopps, dinge(lage), stelleVon("bald").aufgabe !== null) : null;

  /** K36: Stelle eines Zustands, Frist oder Aufgabe, die frühere zuerst (wie `seit` und `bis` der Lage); am selben Tag die
   *  Frist. Ohne Fristzeile im Zustand die Aufgabe. */
  function stelleVon(art: "ueberfaellig" | "bald"): { aufgabe: string | null; datum: string | null } {
    const frist = bald.find((f) => fristZustand(f, heute) === art) ?? null;
    const aufgabe = aufgabeIm(offenAufgaben, art, heute);
    if (aufgabe && (!frist || aufgabe.faellig < frist.datum)) return { aufgabe: aufgabe.id, datum: aufgabe.faellig };
    return { aufgabe: null, datum: frist?.datum ?? null };
  }
  /** Ziel eines Stopps oder einer Zelle: erste passende Zeile der Fristen oder die Zeile der Aufgabe (K36), sonst die Karte
   *  „Bitte prüfen“. */
  function zielVon(art: "ueberfaellig" | "bald" | "vorschlag"): HTMLElement | null {
    if (art === "vorschlag") return document.querySelector<HTMLElement>(".vorschlag");
    const { aufgabe } = stelleVon(art);
    if (aufgabe) return document.querySelector<HTMLElement>(`[data-testid="aufgabe-${aufgabe}"]`);
    return document.querySelector<HTMLElement>(`[data-testid="fristen-liste"] [data-zustand="${art}"]`);
  }
  function datumVon(art: "ueberfaellig" | "bald"): string | null {
    return stelleVon(art).datum;
  }
  /** HW7: das Nächste unter „Bald“ für den Kopfsatz, die Aufgabe mit ihrem Titel oder die Frist mit Betrag, Absender ihres
   *  Briefs und Fristwort; ohne bekannten Absender null (dann bleibt der Satz des Bestands). */
  function naechstes(): Naechstes | null {
    const { aufgabe } = stelleVon("bald");
    const a = aufgabe ? offenAufgaben.find((x) => x.id === aufgabe) : undefined;
    if (a?.faellig) return { datum: a.faellig.slice(0, 10), betrag: null, name: a.titel, wort: null };
    const f = bald.find((x) => fristZustand(x, heute) === "bald");
    if (!f) return null;
    const d = f.dokument_id ? [...pruefenListe, ...(zuletzt.data?.eintraege ?? [])].find((x) => x.id === f.dokument_id) : undefined;
    return { datum: f.datum, betrag: f.betrag ? betrag(f.betrag) : null, name: (d && namen.absender(d.absender_id)) ?? null, wort: FRISTNAME[f.art] ?? FRISTNAME.sonstige ?? null };
  }

  // Rundgang „Zeig sie mir“: Stopp für Stopp, Flug, Scheinwerfer (lebhaft Überall), Blase unterwegs mit Fuß
  function rundgang(i = 0) {
    const stopp = stopps[i];
    if (!stopp) return;
    // HW12: die Station beim Brief zeigt den Kopf der Karte (Absender, Betreff, Frist); die ganze Karte samt Blase passt nicht in
    // jedes Fenster
    const el = stopp === "vorschlag" ? (document.querySelector<HTMLElement>(".vorschlag .v-brief") ?? zielVon(stopp)) : zielVon(stopp);
    if (!el) return rundgang(i + 1);
    const naechster = stopps.slice(i + 1).some((s) => zielVon(s));
    void geist.fliege(el, "zeigen", satzUnterwegs(stopp, stopp === "vorschlag" ? null : datumVon(stopp), heute), {
      erklaert: stopp !== "bald",
      schritt: welt.fassung === "lebhaft" && stopps.length > 1 ? schrittText(i + 1, stopps.length) : undefined,
      weiter: naechster ? () => rundgang(i + 1) : undefined,
      fokus: true,
    });
  }

  // Tipp auf Kachel oder Zelle: Flug nach darf("flugTipp"), sonst Sprung mit kurzem Aufleuchten; Fixkosten öffnet die Finanzen
  function tipp(zelle: "ueberfaellig" | "bald" | "pruefen" | "fixkosten") {
    if (zelle === "fixkosten") return void navigieren("/finanzen");
    if (!ansichtBereit) return;
    const art = zelle === "pruefen" ? "vorschlag" : zelle;
    // FW18: bei gewählter Person ohne Überfälliges fliegt er nicht, er sagt es an seinem Platz
    if (zelle === "ueberfaellig" && gewaehltName && !zielVon(art)) return void sagenAmPlatz(satzNichtsUeberfaellig(gewaehltName), false);
    // W82 Web-5h2 (D-617 Punkt 3, Z30, Z31): ebenso bei „Bald“ und „Zu prüfen“ ohne passende Zeile
    if (zelle === "bald" && gewaehltName && !zielVon(art)) return void sagenAmPlatz(satzNichtsBald(gewaehltName), false);
    if (zelle === "pruefen" && gewaehltName && !zielVon(art)) return void sagenAmPlatz(satzKeinBrief(gewaehltName), false);
    const el = zielVon(art) ?? document.querySelector<HTMLElement>(zelle === "pruefen" ? "#pruefen" : '[data-testid="fristen-modul"]');
    if (!el) return;
    const erklaertHier = (zelle === "ueberfaellig" && lage.ueberfaellig > 0) || (zelle === "pruefen" && lage.pruefen > 0);
    const satzHier = zielVon(art) ? satzUnterwegs(art, art === "vorschlag" ? null : datumVon(art), heute) : undefined;
    void geist.fliege(el, "flugTipp", satzHier, { erklaert: erklaertHier });
  }

  /** Antwort auf Abhaken und „Passt“ (DESIGN-W82 „Antworten auf deine Aktionen“); `rest` wie die Kacheln danach, Fristen
   *  (Überfällig plus Bald) und Briefe (Zu prüfen) getrennt (W82 Web-5b, K17). */
  function antworten(art: "erledigt" | "abgelegt", rest: { fristen: number; briefe: number }, el: HTMLElement | null, warErklaert: boolean, bezug?: HTMLElement | null) {
    const frage = { fassung: welt.fassung, praesenz: welt.praesenz, erklaert: warErklaert };
    const s = satzAntwort(art, { ...rest, aufgaben: aufgabenDran });
    if (darf("geste", frage)) hopsen();
    if (el && darf("flugAntwort", frage)) {
      void geist.fliege(el, "flugAntwort", s, { erklaert: warErklaert, bezug });
      return;
    }
    if (darf("satzAntwort", frage)) {
      // Sicht-Regel: außer Sicht kommt er ins Bild (ruhig als Randgeist), sonst spricht er an seinem Platz
      if (!geist.sage(s, { erklaert: warErklaert, ziel: el, rand: true })) {
        setAntwort(s);
        clearTimeout(antwortUhr.current);
        antwortUhr.current = setTimeout(() => setAntwort(null), 3000);
        geist.halten(3000);
      }
      return;
    }
    if (el) geist.markiere(el, "erledigt");
  }
  /** W82 Web-5h2 (V04, K32): nach dem Abhaken einer Aufgabe dieselbe Antwort wie bei Fristen; zählte sie in Überfällig oder
   *  Bald, steht sie danach nicht mehr an. Er fliegt zur Titelzeile „Aufgaben“, die Zeile selbst blendet aus. */
  function aufgabeErledigt(a: Aufgabe, ziel: HTMLElement | null) {
    const zustand = a.faellig ? fristZustand({ datum: a.faellig.slice(0, 10), erledigt: null }, heute) : "spaeter";
    antworten("erledigt", { fristen: lage.ueberfaellig + lage.bald - (zustand === "spaeter" ? 0 : 1), briefe: lage.pruefen }, ziel, zustand === "ueberfaellig");
  }
  /** FW18: ein Satz ohne Flug (Sprecher-Regel wie die Antworten; bei „Keine Erklärungen“ nichts). */
  function sagenAmPlatz(s: SatzTeil[], warErklaert: boolean) {
    if (!darf("satzAntwort", { fassung: welt.fassung, praesenz: welt.praesenz, erklaert: warErklaert })) return;
    if (geist.sage(s, { erklaert: warErklaert, ziel: null })) return;
    setAntwort(s);
    clearTimeout(antwortUhr.current);
    antwortUhr.current = setTimeout(() => setAntwort(null), 3000);
    geist.halten(3000);
  }
  function hopsen() {
    const figur = document.querySelector<HTMLElement>("[data-geist-szene] .geist-sprung");
    spiel(figur, [{ transform: "none" }, { transform: "translateY(-16px)", offset: 0.4 }, { transform: "none" }], {
      dauer: DAUER.gesten[welt.fassung].hopsen,
      kurve: kurve(kurveFuer("knopf", welt.fassung)),
      art: "lage",
    });
  }

  const fristBlatt = useFristBlatt();
  const blattBrief = (f: Frist) => (f.dokument_id ? [...pruefenListe, ...(zuletzt.data?.eintraege ?? [])].find((x) => x.id === f.dokument_id) : undefined);
  const blattWoher = (f: Frist) => {
    const d = blattBrief(f);
    return fristWoher(d, d && namen.absender(d.absender_id));
  };
  const fristenModul = modulBereit ? (
    <FristenModul
      fristen={jePerson.alle ?? offenFristen}
      heute={heute}
      offen={bald.length}
      // D-704: das Modul misst und startet erst mit dem Auftritt (vorher ohne Messung, Zustand „offen“)
      auftritt={vorBegruessung ? { ...modul, ref: ohneMessen } : modul}
      zeilen={bald.map((f) => {
        const d = f.dokument_id ? [...pruefenListe, ...(zuletzt.data?.eintraege ?? [])].find((x) => x.id === f.dokument_id) : undefined;
        const zustand = fristZustand(f, heute);
        return (
          <FristZeile
            key={f.id}
            frist={f}
            woher={fristWoher(d, d && namen.absender(d.absender_id))}
            onOeffnen={() => fristBlatt.oeffnen(f)}
            onUmgeschaltet={(erledigt, el) => {
              if (erledigt) antworten("erledigt", { fristen: lage.ueberfaellig + lage.bald - (zustand === "spaeter" ? 0 : 1), briefe: lage.pruefen }, el, zustand === "ueberfaellig");
            }}
          />
        );
      })}
    />
  ) : null;
  const seitlich = (
    <>
      {fristenModul}
      {/* W82 Web-5h2 (Frage 47 = A, K44): die Aufgaben stehen immer da, auch bei „Noch keine Post“, mit „Neu“ */}
      {/* R2 W82-Web-5h2: je Ansicht neu, sonst hielte „Nichts offen.“ die Höhe der zuletzt abgehakten Zeile auch bei einer anderen Person */}
      <Aufgaben key={person} nur={person === "alle" ? null : offenAufgaben} onErledigt={aufgabeErledigt} />
    </>
  );

  return (
    <div
      ref={startseite}
      className="startseite"
      data-auftritt={auftritt ?? undefined}
      data-eroeffnung={auftritt && rundflug ? eroeffnungStufe : undefined}
      data-rundflug={rundflug && rundflugSofort ? "sofort" : auftritt && rundflug ? (rundflugWartet ? "wartet" : "gelandet") : undefined}
      data-auftritt-wartet={auftrittWartet ? "" : undefined}
      data-geist-platz-oeffnen=""
      data-testid="startseite"
    >
      <Seite
        titel="Posteingang"
        breite="breit"
        titelVorlesen
        // E14: Kopfzeile mit Profil (Kürzel-Linse) und Datum, rechts die Lupe als Glasknopf (nur unter 744 px)
        // am Schreibtisch (ab 744 px) stehen dort Archiv und Post per Mail, das Profil sitzt in der Seitenleiste
        links={breit ? undefined : <Kopfzeile heute={heute} />}
        rechts={
          breit ? (
            <>
              <Link to="/blaettern" className="t-knopf inline-flex min-h-(--touch) items-center gap-2 rounded-btn px-3 text-pg-text hover:bg-pg-surface-2">
                <Archive size={18} strokeWidth={1.75} aria-hidden />
                Archiv
              </Link>
              <Link to="/post-per-mail" className="t-knopf inline-flex min-h-(--touch) items-center gap-2 rounded-btn px-3 text-pg-text hover:bg-pg-surface-2">
                <AtSign size={18} strokeWidth={1.75} aria-hidden />
                Post per Mail
              </Link>
            </>
          ) : (
            <Lupe />
          )
        }
      >
        <div className="startseite-kopf">
          <GeistSzene
            satz={satz}
            erklaert={istErklaert || fehlerImKopf}
            kopfzeile={lageBereit && ansichtBereit ? <Satz teile={zeileKopf(dinge(lage))} /> : null}
            kennung="kopf-geist"
            nachsatz={hinweisOffen && hinweisDa && !fehlerImKopf ? <PraesenzHinweis onVerstanden={() => setHinweisOffen(false)} /> : undefined}
          >
            {knopf && (
              <button type="button" className="zeig-knopf knopf" data-art={welt.fassung === "ruhig" ? "sekundaer" : "primaer"} data-klein="" onClick={() => rundgang(0)}>
                {knopf}
                {/* N3 (B8, R11 `.zeig svg`): Pfeil nach unten rechts neben dem Wort; beim Auftritt der Blase wächst er nicht mit */}
                {/* W82 Web-6a-N1: die Hülle trägt den Gegenmaßstab der Heimkehr (welt.css), als HTML-Kasten im selben Takt wie die Blase */}
                <span className="zeig-pfeil-huelle">
                  <ArrowDown className="zeig-pfeil" size={welt.fassung === "ruhig" ? 17 : 18} strokeWidth={welt.fassung === "ruhig" ? 2.3 : 2.4} aria-hidden />
                </span>
              </button>
            )}
          </GeistSzene>
          {/* W82 Web-5f (F1 = A, H3): solange eine Person gewählt ist, sagt eine leise Zeile, welche; „Alle zeigen“ hebt sie auf */}
          <AnsichtZeile />
          {lageFehler && !fehlerImKopf && <Fehlertext fehler={lageFehler instanceof ApiFehler ? lageFehler : new Error(fehlerText(lageFehler))} />}
          {lageBereit && <OffenBlock lage={lage} fassung={welt.fassung} praesenz={welt.praesenz} hochzaehlen={hochzaehlen && !zaehlenAus} dunkel={ansichtBereit ? dunkel : DUNKEL_LAEDT} warten={warten} onTipp={tipp} />}
        </div>
        {zuletzt.isPending && <Laedt />}
        {zuletzt.error && <Fehlertext fehler={zuletzt.error instanceof ApiFehler ? zuletzt.error : new Error(fehlerText(zuletzt.error))} />}
        {leer && (
          <Leer icon={Inbox} titel="Noch keine Post" aktion={<Knopf icon={ScanText} onClick={() => navigieren("/erfassen")}>Brief erfassen</Knopf>}>
            Fotografiere einen Brief oder lade ein PDF hoch. Er wird gelesen, eingeordnet und ist danach auffindbar.
          </Leer>
        )}

        <div className="startseite-raster lg:grid lg:grid-cols-[minmax(0,1fr)_var(--w-aside)] lg:items-start lg:gap-x-8">
          <div className="min-w-0">
            {/* Bühnen-Regel: schmal stehen die Fristen (mit den Aufgaben) gleich nach dem Überblick */}
            {!weit && seitlich}
            {(erster || halt) && (
              <Gruppe
                titel={
                  <>
                    Bitte prüfen <Anzahl n={pruefenGesamt} />
                  </>
                }
                titelLesetext={`Bitte prüfen (${pruefenGesamt})`}
                id="pruefen"
              >
                <KartenPlatz halt={halt} karte={erster?.id} />
                {erster && (
                  <Vorschlagskarte
                    key={erster.id}
                    dokument={erster}
                    mehrerePersonen={mehrere}
                    // HW13: „Überall“ ohne Kartenkopf (den Satz sagt der Gaist), sonst still
                    stimme={kopfVorschlag(welt.praesenz)}
                    stimmeStill
                    flaecheHalten
                    // der Brief geht aus „Zu prüfen“; seine Fristen bis 7 Tage (auch überfällige) stehen danach in „Überfällig“ oder „Bald“.
                    // HW9: er antwortet an der Stelle des Tipps (Knopfreihe), die Karte ist sein Bezug und hält ihre Fläche
                    onAbgelegt={(karte, kategorie, neu) => {
                      antworten("abgelegt", { fristen: lage.ueberfaellig + lage.bald + neu.filter((f) => tageBis(f.datum, heute) <= 7).length, briefe: lage.pruefen - 1 }, karte?.querySelector<HTMLElement>(".v-knoepfe-reihe") ?? karte, true, karte);
                      // Frage 66 = B: der gehaltene Platz zeigt den Erfolgszustand der Karte (Kopf, Haken, „Abgelegt in …“)
                      if (karte) halten(erster.id, karte.getBoundingClientRect().height, { name: kategorie, kopf: karte.querySelector<HTMLElement>(".v-brief") });
                    }}
                  />
                )}
                {weitere.map((d) => (
                  <DokumentZeile key={d.id} dokument={d} namen={namen} frist={fristJeDokument.get(d.id)} />
                ))}
              </Gruppe>
            )}
            {/* F14: wartet eine Karte der Start-Runde, fuehrt ein schlichter Hinweis zur Stapel-Ansicht
                (Kennenlernen.tsx). Oben steht der neueste Brief, Kennenlernen nimmt je Kategorie den mit
                der niedrigsten Konfidenz: nur wenn die einzige wartende Karte genau die Vorschlagskarte
                oben ist, reicht die Karte (Vergleich ueber die Dokument-ID). */}
            {kennenlernen?.aktiv && kennenlernen.karten.length > 0 && !(kennenlernen.karten.length === 1 && kennenlernen.karten[0]?.dokument_id === erster?.id) && (
              <p id="kennenlernen" className="t-footnote mt-2 flex flex-wrap items-center gap-x-2 px-4 text-pg-text-2">
                <span>
                  Kennenlernen: {kennenlernen.karten.length === 1 ? "1 Brief wartet" : `${kennenlernen.karten.length} Briefe warten`}, {Math.min(kennenlernen.bestaetigt, kennenlernen.ziel)} von {kennenlernen.ziel} angesehen.
                </span>
                <Link to="/kennenlernen" className="inline-flex min-h-(--touch) items-center text-pg-accent-text hover:opacity-70">
                  Zusammen durchgehen
                </Link>
              </p>
            )}
            <WeitereLaden geladen={pruefenListe.length} gesamt={pruefenGesamt} laedt={pruefenSeiten.isFetchingNextPage} onLaden={() => void pruefenSeiten.fetchNextPage()} />

            {ich?.bestaetigt === false && (
              <Gruppe id="bestaetigen">
                <Zeile zu="/einstellungen#konto" mitKachel chevron vorne={<Kachel schluessel={null} icon={MailWarning} />}>
                  <span className="t-body">Mailadresse bestätigen</span>
                  <span className="t-footnote text-pg-text-2">Tipp auf den Link in der Mail nach der Registrierung</span>
                </Zeile>
              </Gruppe>
            )}

            {rueckfragen > 0 && (
              <Gruppe id="mail-rueckfragen">
                <Zeile zu="/post-per-mail#rueckfragen" mitKachel chevron vorne={<Kachel schluessel={null} icon={Mail} />}>
                  <span className="t-body">{rueckfragen === 1 ? "1 Mail mit Rückfrage" : `${rueckfragen} Mails mit Rückfrage`}</span>
                  <span className="t-footnote text-pg-text-2">Per Mail gekommen, aber nicht von selbst übernommen</span>
                </Zeile>
              </Gruppe>
            )}

            {inArbeit.length > 0 && (
              <Gruppe titel="Wird erkannt" id="erkennung">
                {inArbeit.map((d) => (
                  <DokumentZeile key={d.id} dokument={d} namen={namen} />
                ))}
              </Gruppe>
            )}

            {archiviert.length > 0 && (
              // N4 (D-453, R11 `.abschnitt-kopf` mit `.alle`): Titel „Neue Post“ mit Anzahl, rechts „Alle ›“ ins Archiv; die
              // Zeile „Archiv nach Kategorien“ geht in diesem Weg auf (die Vorlage zeigt sie im Block nicht). W82 Web-8 W8-1
              // (D-1443, wie iOS HD-3): „Alle ›“ öffnet „Alle abgelegten Briefe“; die Kategorien bleiben über Akten erreichbar
              <Gruppe
                titel={
                  <>
                    {NEUE_POST.titel} <Anzahl n={archiviert.length} />
                  </>
                }
                titelLesetext={NEUE_POST.titelLesetext(archiviert.length)}
                id="archiviert"
                listeFokussierbar
                aktion={
                  <Link to={ALLE_ABGELEGT} className="fm-alle gruppe-alle w-knopf-klein" aria-label={NEUE_POST.alleLesetext}>
                    {NEUE_POST.alle}
                    <ChevronRight size={18} strokeWidth={2} aria-hidden />
                  </Link>
                }
              >
                {archiviert.map((d) => (
                  <DokumentZeile key={d.id} dokument={d} namen={namen} frist={fristJeDokument.get(d.id)} post />
                ))}
              </Gruppe>
            )}
          </div>
          {weit && (
            <aside aria-label="Fristen und Aufgaben" className="min-w-0">
              {seitlich}
            </aside>
          )}
        </div>
        {/* W82 Web-5h1 (F2 = A): Blatt „Frist“ aus dem Fristen-Modul */}
        <FristNeuBlatt
          offen={fristBlatt.offen}
          frist={fristBlatt.frist}
          brief={fristBlatt.frist && blattBrief(fristBlatt.frist)}
          woher={fristBlatt.frist && blattWoher(fristBlatt.frist)}
          onSchliessen={fristBlatt.schliessen}
        />
        {/* W81 B5: einmal je Sitzung das Blatt zur neuen Fassung der Einwilligung in „Alle meine Briefe“ */}
        <FreigabeRueckfall />
      </Seite>
    </div>
  );
}

/** N4 (D-453): Menge und Texte des Blocks „Neue Post“ (wortgleich für iOS und Android). */
const NEUE_POST_MENGE = 3;
const NEUE_POST = {
  titel: "Neue Post",
  titelLesetext: (n: number) => `Neue Post (${n})`,
  alle: "Alle",
  alleLesetext: "Alle Briefe im Archiv",
} as const;

/** Anzahl neben dem Abschnittstitel (R11 `.anzahl`): sichtbar „1“ in Bricolage 20 / 600 und Nebentext; der Name des
 *  Abschnitts bleibt der des Bestands („Bitte prüfen (1)“, „Aufgaben (2)“, `titelLesetext` der Gruppe). */
/** `nur`: bei gewählter Personen-Ansicht ihre Aufgaben (W82 Web-5f, `usePersonFristen`), sonst alle. W82 Web-5h2 (Frage 47 = A,
 *  K44): die Gruppe steht immer, auch ohne Post; beim Abhaken der letzten Aufgabe blendet nur ihre Zeile aus und „Nichts offen.“
 *  am selben Platz ein (Überblendung ohne Höhenbewegung, `aufgabe-geht` in welt.css), der Fokus geht auf „Neu“. */
function Aufgaben(props: { nur: Aufgabe[] | null; onErledigt?: (a: Aufgabe, ziel: HTMLElement | null) => void }) {
  const { data: aufgaben, error } = useAufgaben();
  const erledigt = useAufgabeErledigt();
  const toast = useToast();
  const [neu, setNeu] = useState(false);
  // die zuletzt abgehakte Aufgabe, solange ihre Zeile ausblendet (sie ist dann schon aus der Antwort des Servers)
  const [geht, setGeht] = useState<Aufgabe | null>(null);
  // R1 W82-Web-5h2: Höhe der letzten Zeile beim Abhaken; „Nichts offen.“ hält sie, sonst sprang die Gruppe nach der Blende
  // (langer Titel, große Schrift: 134 → 50 px, bei 200 % 196 → 92 px)
  const [platz, setPlatz] = useState<number | null>(null);
  const neuKnopf = useRef<HTMLButtonElement>(null);
  const gehtZeile = useRef<HTMLLIElement>(null);
  const leerZeile = useRef<HTMLLIElement>(null);
  const ids = props.nur ? new Set(props.nur.map((a) => a.id)) : null;
  const offen = (aufgaben ?? []).filter((a) => !a.erledigt && (!ids || ids.has(a.id)));
  const ueberblenden = geht !== null && offen.length === 0;
  // W80 (W3): „Zur Aufgabe“ an der Karte des Gaist fuehrt hierher (`/#aufgaben`)
  useAnker("aufgaben", Boolean(aufgaben || error));

  function abhaken(a: Aufgabe) {
    const letzte = offen.length === 1;
    const hoehe = letzte ? (document.querySelector(`[data-testid="aufgabe-${a.id}"]`)?.getBoundingClientRect().height ?? null) : null;
    erledigt.mutate(
      { id: a.id, erledigt: true },
      {
        onSuccess: () => {
          if (letzte) {
            setGeht(a);
            setPlatz(hoehe);
          }
          // R2 (HW12, D-666): Ziel ist die ganze Titelzeile, wie eine Fristzeile: er steht an ihrer rechten Ecke im Platz, die
          // Blase links daneben in derselben Spalte (am schmalen Titel stand er mitten in der Spalte, und die Blase ragte am
          // Schreibtisch über „Neue Post“)
          const titel = document.getElementById("aufgaben");
          props.onErledigt?.(a, titel?.closest<HTMLElement>(".gruppe-kopf") ?? titel);
        },
        onError: (e) => toast(fehlerText(e), planWeg(e)),
      },
    );
  }

  // Überblendung: die Zeile geht (Deckkraft 1 → 0), „Nichts offen.“ kommt (0 → 1), beide am selben Platz; danach ist nur
  // noch „Nichts offen.“ da. Der Fokus lag auf der Zeile, die jetzt ruht: er geht auf „Neu“, nie auf body
  useEffect(() => {
    if (!ueberblenden) return;
    const aktiv = document.activeElement;
    if (!aktiv || aktiv === document.body || gehtZeile.current?.contains(aktiv)) neuKnopf.current?.focus({ preventScroll: true });
    const fassung = document.documentElement.getAttribute("data-fassung") === "ruhig" ? "ruhig" : "lebhaft";
    const dauer = DAUER.wechsel.bildschirm[fassung];
    const aus = blende(gehtZeile.current, 1, 0, dauer.raus);
    blende(leerZeile.current, 0, 1, dauer.rein);
    if (!aus) return void setGeht(null);
    aus.onfinish = () => setGeht(null);
    return () => aus.cancel();
  }, [ueberblenden]);

  return (
    <>
      <Gruppe
        titel={
          <>
            Aufgaben <Anzahl n={offen.length} />
          </>
        }
        titelLesetext={`Aufgaben (${offen.length})`}
        id="aufgaben"
        aktion={
          <span className="flex flex-wrap items-center justify-end gap-x-4">
            <button ref={neuKnopf} type="button" onClick={() => setNeu(true)} className={LINK}>
              <Plus size={18} strokeWidth={1.75} aria-hidden />
              Neu
            </button>
            {/* W82 Web-7, Nachzügler 2 (D-1273): offene und erledigte Aufgaben auf eigener Seite, Muster „Alle ›“ der Fristen */}
            <Link to="/aufgaben" className={LINK} aria-label="Alle Aufgaben" data-testid="aufgaben-alle">
              Alle
              <ChevronRight size={18} strokeWidth={2} aria-hidden />
            </Link>
          </span>
        }
      >
        {error && (
          <li className="px-4 py-3">
            <Fehlertext fehler={error instanceof ApiFehler ? error : new Error(fehlerText(error))} />
          </li>
        )}
        {ueberblenden && (
          <li ref={gehtZeile} className="zeile relative aufgabe-geht" aria-hidden inert data-testid="aufgabe-geht">
            <div className={ZEILE_KLASSE}>
              <span aria-hidden className="mt-0.5 size-(--kaestchen) shrink-0 rounded-sm shadow-(--pg-feld-rand)" />
              <span className="zeile-text flex min-w-0 flex-1 flex-col justify-center">
                <span className="t-body">{geht.titel}</span>
              </span>
              {geht.faellig && (
                <span className="zeile-hinten flex shrink-0 justify-end">
                  <FristWort datum={geht.faellig} />
                </span>
              )}
            </div>
          </li>
        )}
        {!error && aufgaben && offen.length === 0 && (
          <li ref={leerZeile} className="zeile relative aufgaben-leer" data-testid="aufgaben-leer" style={platz ? { minHeight: platz } : undefined}>
            <div className={ZEILE_KLASSE}>
              <span className="zeile-text flex min-w-0 flex-1 flex-col justify-center">
                <span className="t-body text-pg-text-2">Nichts offen.</span>
              </span>
            </div>
          </li>
        )}
        {offen.map((a) => (
          <Zeile
            key={a.id}
            onClick={() => abhaken(a)}
            label={`${a.titel} als erledigt markieren`}
            kennung={`aufgabe-${a.id}`}
            vorne={<span aria-hidden className="mt-0.5 size-(--kaestchen) shrink-0 rounded-sm shadow-(--pg-feld-rand)" />}
            hinten={a.faellig ? <FristWort datum={a.faellig} /> : undefined}
          >
            <span className="t-body">{a.titel}</span>
          </Zeile>
        ))}
      </Gruppe>
      <NeueAufgabe offen={neu} onSchliessen={() => setNeu(false)} />
    </>
  );
}

/** Blende der Deckkraft (K44): Dauer und Kurve aus den Tokens (`wechsel.bildschirm`, expo); reduzierte Bewegung 150 ms ohne
 *  Lage (DESIGN-W82 „Reduzierte Bewegung“, FW22 Regel 6), `?still` sofort im Endzustand. */
function blende(el: HTMLElement | null, von: number, bis: number, dauer: number): Animation | null {
  if (!el || still() || typeof el.animate !== "function") return null;
  const reduziert = bewegungReduziert();
  return el.animate([{ opacity: von }, { opacity: bis }], { duration: reduziert ? BLENDE_REDUZIERT_W82 : dauer, easing: reduziert ? "linear" : KURVE.expo, fill: "both" });
}
